import type { ReactNode } from 'react'
import { Link, useRouteError } from 'react-router-dom'
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/primitives'
import { useAuthStore } from '@/lib/store'

/**
 * errorElement на два уровня роутера — см. router.tsx.
 *
 * У сорвавшегося динамического import() ленивого чанка две частые причины,
 * и обе дают один и тот же TypeError:
 *  - офлайн: чанка раздела, за которым не заходили при сети, нет в кэше
 *    Service Worker;
 *  - вкладка открыта старой версией, а её чанков уже нет на сервере —
 *    deploy-web.sh переключает `current` на новый релиз и подчищает старые
 *    сразу, без grace-периода на уже открытые вкладки. При частых выкладках
 *    (несколько в день) это самый частый случай на практике, чаще офлайна.
 *
 * navigator.onLine различает их: сеть есть — значит дело в устаревшей
 * версии, а не в подключении, и совет «проверьте интернет» был бы просто
 * неверным. Без errorElement React Router рисует свой дефолтный экран
 * для разработчиков поверх всего приложения — это и есть «Unexpected
 * Application Error».
 *
 * Перезагрузка страницы — единственное настоящее восстановление в обоих
 * случаях: упавший React.lazy() держит отклонённый промис до конца жизни
 * вкладки, повторный рендер того же маршрута ничего не чинит. Офлайн сама
 * кнопка не бессмысленна: оболочку (index.html и app-чанк) Service Worker
 * отдаёт из кэша, только результат будет тем же, пока не появится сеть.
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|failed to fetch/i.test(message)
}

function describeError(error: unknown): { icon: ReactNode; title: string; description: string } {
  if (isChunkLoadError(error)) {
    if (navigator.onLine) {
      return {
        icon: <RefreshCw size={48} />,
        title: 'Доступна новая версия',
        description:
          'Пока вкладка была открыта, вышло обновление, и прежние файлы уже недоступны. Обновите страницу.',
      }
    }
    return {
      icon: <WifiOff size={48} />,
      title: 'Не удалось открыть раздел',
      description: 'Похоже, пропало соединение с интернетом. Проверьте подключение и обновите страницу.',
    }
  }
  return {
    icon: <AlertTriangle size={48} />,
    title: 'Что-то пошло не так',
    description: 'Попробуйте обновить страницу.',
  }
}

const reload = () => window.location.reload()

/**
 * Ловит всё, что не поймал SectionErrorBoundary внутри кабинета:
 * лендинг, юридические страницы, вход и регистрацию. Полноэкранный —
 * навигации рядом нет, поэтому кроме перезагрузки даёт ссылку на безопасное
 * место, как и 404-страница.
 */
export function RootErrorBoundary() {
  const error = useRouteError()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { icon, title, description } = describeError(error)

  return (
    <div className="flex h-dvh items-center justify-center bg-background px-6">
      <EmptyState
        icon={icon}
        title={title}
        description={description}
        action={
          <div className="flex flex-col items-center gap-2">
            <Button icon={<RefreshCw size={18} />} onClick={reload}>
              Обновить
            </Button>
            <Button asChild variant="ghost">
              <Link to={isAuthenticated ? '/lk/bible' : '/'}>
                {isAuthenticated ? 'В приложение' : 'На главную'}
              </Link>
            </Button>
          </div>
        }
      />
    </div>
  )
}

/**
 * Ловит сорвавшийся раздел кабинета, не задевая AppShell: тот стоит выше
 * этого узла в дереве маршрутов и остаётся смонтированным, значит таб-бар
 * или боковая панель никуда не пропадают и открыть другой раздел можно
 * одним тапом, а не только системной кнопкой «назад».
 */
export function SectionErrorBoundary() {
  const error = useRouteError()
  const { icon, title, description } = describeError(error)

  return (
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      action={
        <Button icon={<RefreshCw size={18} />} onClick={reload}>
          Обновить
        </Button>
      }
    />
  )
}
